import { useState, useRef, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface SignatureSectionProps {
  onSignatureChange: (signature: string) => void;
  onKvkkChange: (accepted: boolean) => void;
  onSalesRepChange: (repId: string) => void;
}

interface KVKKSection {
  heading: string;
  content: string;
}

interface KVKKText {
  id: string;
  title: string;
  content: KVKKSection[];
}

interface SalesRepresentative {
  id: string;
  name: string;
  is_active: boolean;
}

export default function SignatureSection({ onSignatureChange, onKvkkChange, onSalesRepChange }: SignatureSectionProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [kvkkAccepted, setKvkkAccepted] = useState(false);
  const [showKvkkModal, setShowKvkkModal] = useState(false);
  const [kvkkText, setKvkkText] = useState<KVKKText | null>(null);
  const [loading, setLoading] = useState(true);
  const [salesReps, setSalesReps] = useState<SalesRepresentative[]>([]);
  const [selectedRep, setSelectedRep] = useState('');
  const [loadingReps, setLoadingReps] = useState(true);

  useEffect(() => {
    fetchKVKKText();
    fetchSalesReps();
    initializeCanvas();
    
    // Listen for clear signature event
    const handleClearEvent = () => {
      clearSignature();
      setKvkkAccepted(false);
      setSelectedRep('');
      onKvkkChange(false);
      onSalesRepChange('');
    };
    
    window.addEventListener('clearSignature', handleClearEvent);
    
    return () => {
      window.removeEventListener('clearSignature', handleClearEvent);
    };
  }, []);

  const initializeCanvas = () => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    if (ctx) {
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  };

  const fetchSalesReps = async () => {
    try {
      const { data, error } = await supabase
        .from('sales_representatives')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      if (data) {
        setSalesReps(data);
      }
    } catch (error) {
      console.error('Satış temsilcileri yüklenirken hata:', error);
    } finally {
      setLoadingReps(false);
    }
  };

  const fetchKVKKText = async () => {
    try {
      const { data, error } = await supabase
        .from('kvkk_text')
        .select('*')
        .eq('is_active', true)
        .single();

      if (error) throw error;
      if (data) {
        setKvkkText(data);
      }
    } catch (error) {
      console.error('KVKK metni yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleKvkkChange = (checked: boolean) => {
    setKvkkAccepted(checked);
    onKvkkChange(checked);
  };

  const handleSalesRepChange = (repId: string) => {
    setSelectedRep(repId);
    onSalesRepChange(repId);
  };

  const getCoordinates = (e: React.MouseEvent<HTMLCanvasElement> | React.Touch) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const clientX = 'clientX' in e ? e.clientX : e.clientX;
    const clientY = 'clientY' in e ? e.clientY : e.clientY;
    
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const saveSignature = () => {
    if (!canvasRef.current) return;
    const signatureData = canvasRef.current.toDataURL();
    onSignatureChange(signatureData);
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(x, y);
      setIsDrawing(true);
    }
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current || !isDrawing) return;
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    if (ctx) {
      ctx.lineTo(x, y);
      ctx.stroke();
    }
  };

  const stopDrawing = () => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    if (ctx) {
      ctx.closePath();
    }
    setIsDrawing(false);
    saveSignature();
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;
    e.preventDefault();
    const { x, y } = getCoordinates(e.touches[0]);
    const ctx = canvasRef.current.getContext('2d');
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(x, y);
      setIsDrawing(true);
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current || !isDrawing) return;
    e.preventDefault();
    const { x, y } = getCoordinates(e.touches[0]);
    const ctx = canvasRef.current.getContext('2d');
    if (ctx) {
      ctx.lineTo(x, y);
      ctx.stroke();
    }
  };

  const handleTouchEnd = () => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    if (ctx) {
      ctx.closePath();
    }
    setIsDrawing(false);
    saveSignature();
  };

  const clearSignature = () => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
    onSignatureChange('');
  };

  return (
    <div className="space-y-6">
      {/* Sales Representative Selection */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Satış Temsilcisi <span className="text-red-500">*</span>
        </label>
        {loadingReps ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <select
            value={selectedRep}
            onChange={(e) => handleSalesRepChange(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700 bg-white cursor-pointer"
            required
          >
            <option value="">Satış temsilcisi seçiniz...</option>
            {salesReps.map((rep) => (
              <option key={rep.id} value={rep.id}>
                {rep.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Signature Canvas */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          İmza <span className="text-red-500">*</span>
        </label>
        <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-gray-50">
          <canvas
            ref={canvasRef}
            width={600}
            height={200}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            className="w-full cursor-crosshair touch-none"
            style={{ touchAction: 'none' }}
          />
        </div>
        <button
          type="button"
          onClick={clearSignature}
          className="mt-3 px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-eraser-line mr-2"></i>
          İmzayı Temizle
        </button>
      </div>

      {/* KVKK Consent */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-sm border border-blue-200 p-6">
        <div className="flex items-start space-x-3">
          <input
            type="checkbox"
            id="kvkk-consent"
            checked={kvkkAccepted}
            onChange={(e) => handleKvkkChange(e.target.checked)}
            className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
          />
          <label htmlFor="kvkk-consent" className="text-sm text-gray-700 cursor-pointer">
            <button
              type="button"
              onClick={() => setShowKvkkModal(true)}
              className="text-blue-600 hover:text-blue-700 underline font-medium"
            >
              {loading ? 'KVKK Aydınlatma Metni' : (kvkkText?.title || 'KVKK Aydınlatma Metni')}
            </button>
            'ni okudum ve kabul ediyorum. <span className="text-red-500">*</span>
          </label>
        </div>
      </div>

      {/* KVKK Modal */}
      {showKvkkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-t-2xl flex items-center justify-between">
              <h3 className="text-2xl font-bold">
                {loading ? 'KVKK Aydınlatma Metni' : (kvkkText?.title || 'KVKK Aydınlatma Metni')}
              </h3>
              <button
                onClick={() => setShowKvkkModal(false)}
                className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto flex-1">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : kvkkText ? (
                <div className="space-y-6">
                  {kvkkText.content.map((section, index) => (
                    <div key={index}>
                      <h4 className="text-lg font-bold text-gray-800 mb-2">
                        {section.heading}
                      </h4>
                      <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                        {section.content}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  KVKK metni yüklenemedi. Lütfen daha sonra tekrar deneyiniz.
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200">
              <button
                onClick={() => setShowKvkkModal(false)}
                className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all font-semibold whitespace-nowrap cursor-pointer"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
